import * as _ from 'lodash-es';
import { useMemo, useReducer } from 'react';
import { SelectTypes } from './controls/RowColumnControl';
import { parseCellIdentity, stringifyCellIdentity } from './utils';

export enum SelectionType {
  Cell = 'CELL',
  Row = 'ROW',
  Column = 'COLUMN',
  Table = 'TABLE',
}

export interface RowSelection {
  type: SelectionType.Row;
  rows: number[];
}

export interface ColumnSelection {
  type: SelectionType.Column;
  columns: number[];
}

export interface TableSelection {
  type: SelectionType.Table;
}

interface CellSelection {
  type: SelectionType.Cell;
  identity: string;
}

export const getRowColumnItems = (
  selection: Selection | null,
): [number[], boolean] | [null, null] => {
  switch (selection?.type) {
    case SelectionType.Row:
      return [selection.rows, true];
    case SelectionType.Column:
      return [selection.columns, false];
    default:
      return [null, null];
  }
};

export const isRowOrColumnSelected = ({
  index,
  isRow,
  selection,
}: {
  index: number;
  isRow: boolean;
  selection: Selection | null;
}) => {
  switch (selection?.type) {
    case SelectionType.Table:
      return true;
    case SelectionType.Row:
      return isRow && selection.rows.includes(index);
    case SelectionType.Column:
      return !isRow && selection.columns.includes(index);
    default:
      return false;
  }
};

const shiftRange = (originalIndex: number, index: number, insert: boolean) => {
  if (originalIndex < index) return originalIndex;
  if (insert) {
    return originalIndex + 1;
  }
  if (originalIndex === index) {
    return null;
  }
  return originalIndex - 1;
};

const notNull = <T>(value: T | null): value is T => value !== null;

const toggleItemFromArray = <T>(array: T[], element: T) =>
  array.includes(element) ? _.without(array, element) : [...array, element];

const getConsecutiveRange = (integers: number[]) => {
  const [min, max] = [_.min(integers), _.max(integers)];
  if (min == null || max == null) return [];

  const ints = [];
  for (let i = min; i <= max; i += 1) {
    ints.push(i);
  }
  return ints;
};

export type Selection =
  | RowSelection
  | ColumnSelection
  | TableSelection
  | CellSelection;

type Action =
  | { type: 'resetSelection' }
  | { type: 'shiftIndex'; isRow: boolean; index: number; insert: boolean }
  | {
      type: 'selectRowOrColumn';
      isRow: boolean;
      index: number;
      selectType: SelectTypes;
    }
  | { type: 'selectCell'; identity: string }
  | { type: 'selectTable' };

const reducer = (state: Selection | null, action: Action): Selection | null => {
  switch (action.type) {
    case 'resetSelection':
      return null;
    case 'shiftIndex': {
      switch (state?.type) {
        case SelectionType.Column:
          return action.isRow
            ? state
            : {
                ...state,
                columns: state.columns
                  .map((item) => shiftRange(item, action.index, action.insert))
                  .filter(notNull),
              };
        case SelectionType.Row:
          return action.isRow
            ? {
                ...state,
                rows: state.rows
                  .map((item) => shiftRange(item, action.index, action.insert))
                  .filter(notNull),
              }
            : state;
        case SelectionType.Cell: {
          const [row, column] = parseCellIdentity(state.identity);
          const newIndex = shiftRange(
            action.isRow ? row : column,
            action.index,
            action.insert,
          );
          if (newIndex === null) return null;
          const identity = stringifyCellIdentity(
            action.isRow ? newIndex : row,
            action.isRow ? column : newIndex,
          );
          return { ...state, identity };
        }
        default:
          return state;
      }
    }
    case 'selectCell':
      return state?.type === SelectionType.Cell &&
        state.identity === action.identity
        ? state
        : { type: SelectionType.Cell, identity: action.identity };
    case 'selectRowOrColumn': {
      const selectionType = action.isRow
        ? SelectionType.Row
        : SelectionType.Column;
      if (
        !state ||
        state.type !== selectionType ||
        action.selectType === SelectTypes.Single
      ) {
        return action.isRow
          ? {
              type: SelectionType.Row,
              rows: [action.index],
            }
          : { type: SelectionType.Column, columns: [action.index] };
      }
      const indexes =
        state.type === SelectionType.Row ? state.rows : state.columns;
      const newIndexes =
        action.selectType === SelectTypes.Multiple
          ? toggleItemFromArray(indexes, action.index)
          : getConsecutiveRange(indexes.concat(action.index));

      return state.type === SelectionType.Row
        ? { ...state, rows: newIndexes }
        : { ...state, columns: newIndexes };
    }
    case 'selectTable':
      return { type: SelectionType.Table };
    default:
      break;
  }
  return state;
};

const useTableSelection = () => {
  const [selection, dispatch] = useReducer(reducer, null);
  return {
    selection,
    actions: useMemo(
      () => ({
        resetSelection: () => {
          dispatch({ type: 'resetSelection' });
        },
        shiftIndex: (
          index: number,
          { isRow, insert }: { isRow: boolean; insert: boolean },
        ) => {
          dispatch({ type: 'shiftIndex', index, insert, isRow });
        },
        selectRowOrColumn: (
          index: number,
          { isRow, selectType }: { isRow: boolean; selectType: SelectTypes },
        ) => {
          dispatch({
            type: 'selectRowOrColumn',
            index,
            isRow,
            selectType,
          });
        },
        selectCell: (identity: string) => {
          dispatch({ type: 'selectCell', identity });
        },
        selectTable: () => {
          dispatch({ type: 'selectTable' });
        },
      }),
      [dispatch],
    ),
  };
};

export default useTableSelection;
